import { catchError, firstValueFrom, map, Observable, of } from "rxjs";
import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import {
    AccionesItemDTO,
    ComboMovilDTO,
    EncuestaCargaDTO,
    EncuestaDTO,
    EncuestaVaciaDTO,
    IAccionesItemDTO,
    IEncuestaDTO,
    IItemPreguntaDTO,
    ImagenURLDTO,
    IPaginadoDTO,
    IPreguntaDTO,
    ITemplateDTO,
    ItemPreguntaDTO,
    PaginadoDTO,
    PreguntaDTO,
    PruebaInternaDTO,
    TemplateDTO
} from "@DTOS";
import { Constants } from "@constants";
import { Nilable } from "@shared/utils";

@Injectable({
    providedIn: "root"
})
export class EncuestasServiceBackend {
    constructor(private httpClient: HttpClient) {}

    public async getEncuestas(pagina: number, cantidad: number): Promise<PaginadoDTO<EncuestaDTO>> {
        const encuestasPaginados = await firstValueFrom(
            this.httpClient.get<IPaginadoDTO<IEncuestaDTO>>(
                `${Constants.apiURL}/combos?page=${pagina}&size=${cantidad}`
            )
        ).catch(() => new PaginadoDTO<IEncuestaDTO>().toJSON());

        const encuestas = encuestasPaginados.results?.map(encuesta => new EncuestaDTO(encuesta)) ?? [];
        return new PaginadoDTO({...encuestasPaginados, results: encuestas});
    }

    public async getEncuestasQuery(query: string, pagina: number, cantidad: number): Promise<PaginadoDTO<EncuestaDTO>> {
        const encuestasPaginados = await firstValueFrom(
            this.httpClient.get<IPaginadoDTO<IEncuestaDTO>>(
                `${Constants.apiURL}/combos?page=${pagina}&size=${cantidad}&q=${encodeURIComponent(query)}`
            )
        );

        const encuestas = encuestasPaginados.results.map(encuesta => new EncuestaDTO(encuesta));
        return new PaginadoDTO({...encuestasPaginados, results: encuestas});
    }

    public async postEncuestaRaw(encuesta: EncuestaCargaDTO): Promise<Nilable<EncuestaDTO>> {
        const response = await firstValueFrom(
            this.httpClient.post<IEncuestaDTO>(
                `${Constants.apiURL}/combo-bulk-clone`, encuesta.toJSON()
            ).pipe(
                catchError(() => of(null))
            )
        );

        return response == null ? null : new EncuestaDTO(response);
    }

    public async postEncuesta(encuesta: EncuestaVaciaDTO): Promise<EncuestaDTO> {
        const response = await firstValueFrom(
            this.httpClient.post<IEncuestaDTO>(
                `${Constants.apiURL}/combos`, encuesta.toJSON()
            )
        );

        return new EncuestaDTO(response);
    }

    public async putEncuesta(encuesta: EncuestaDTO): Promise<EncuestaDTO> {
        const response = await firstValueFrom(
            this.httpClient.put<IEncuestaDTO>(
                `${Constants.apiURL}/combos/${encuesta.id}`, encuesta.toJSON()
            )
        );

        return new EncuestaDTO(response);
    }

    public async getEncuesta(id: number): Promise<EncuestaDTO> {
        const response = await firstValueFrom(this.httpClient.get<IEncuestaDTO>(`${Constants.apiURL}/combos/${id}`));

        return new EncuestaDTO(response);
    }

    public async getEncuestaPublica(id: string): Promise<EncuestaDTO> {
        const response = await firstValueFrom(this.httpClient.get<IEncuestaDTO>(`${Constants.apiURLV1}/combo/${id}`));

        return new EncuestaDTO(response);
    }

    public async deleteEncuesta(encuesta: EncuestaDTO): Promise<void> {
        await firstValueFrom(this.httpClient.delete(`${Constants.apiURL}/combos/${encuesta.id}`));
    }

    public async getPreguntasEncuesta(encuesta: EncuestaDTO): Promise<PreguntaDTO[]> {
        const response = await firstValueFrom(
            this.httpClient.get<IPreguntaDTO[]>(
                `${Constants.apiURL}/combos/${encuesta.id}/questions`
            )
        );

        return response.map(pregunta => new PreguntaDTO(pregunta));
    }

    public async getPreguntasAfinidad(project: number): Promise<PreguntaDTO[]> {
        const response = await firstValueFrom(
            this.httpClient.get<IPreguntaDTO[]>(
                `${Constants.apiURL}/bigml-projects/${project}/afinity_questions`
            )
        );

        return response.map(pregunta => new PreguntaDTO(pregunta));
    }

    public async postPreguntaEncuesta(encuesta: EncuestaDTO, pregunta: PreguntaDTO): Promise<PreguntaDTO> {
        const response = await firstValueFrom(
            this.httpClient.post<IPreguntaDTO>(
                `${Constants.apiURL}/combos/${encuesta.id}/questions`, pregunta.toJSON()
            )
        );

        return new PreguntaDTO(response);
    }

    public async getPreguntaEncuesta(encuesta: EncuestaDTO, preguntaId: number): Promise<PreguntaDTO> {
        const response = await firstValueFrom(
            this.httpClient.get<IPreguntaDTO>(
                `${Constants.apiURL}/combos/${encuesta.id}/questions/${preguntaId}`
            )
        );

        return new PreguntaDTO(response);
    }

    public async putPreguntaEncuesta(encuesta: EncuestaDTO, pregunta: PreguntaDTO): Promise<PreguntaDTO> {
        const response = await firstValueFrom(
            this.httpClient.put<IPreguntaDTO>(
                `${Constants.apiURL}/combos/${encuesta.id}/questions/${pregunta.id}`, pregunta.toJSON()
            )
        );

        return new PreguntaDTO(response);
    }

    public async deletePreguntaEncuesta(encuesta: EncuestaDTO, pregunta: PreguntaDTO): Promise<void> {
        await firstValueFrom(this.httpClient.delete(`${Constants.apiURL}/combos/${encuesta.id}/questions/${pregunta.id}`));
    }

    public async postItemPregunta(
        encuesta: EncuestaDTO, pregunta: PreguntaDTO, item: ItemPreguntaDTO
    ): Promise<ItemPreguntaDTO> {
        const response = await firstValueFrom(
            this.httpClient.post<IItemPreguntaDTO>(
                `${Constants.apiURL}/combos/${encuesta.id}/questions/${pregunta.id}/items`, item.toJSON()
            )
        );

        return new ItemPreguntaDTO(response);
    }

    public async putItemPregunta(
        encuesta: EncuestaDTO, pregunta: PreguntaDTO, item: ItemPreguntaDTO
    ): Promise<ItemPreguntaDTO> {
        const response = await firstValueFrom(
            this.httpClient.put<IItemPreguntaDTO>(
                `${Constants.apiURL}/combos/${encuesta.id}/questions/${pregunta.id}/items/${item.id}`, item.toJSON()
            )
        );

        return new ItemPreguntaDTO(response);
    }

    public async deleteItemPregunta(
        encuesta: EncuestaDTO, pregunta: PreguntaDTO, item: ItemPreguntaDTO
    ): Promise<void> {
        await firstValueFrom(
            this.httpClient.delete(
                `${Constants.apiURL}/combos/${encuesta.id}/questions/${pregunta.id}/items/${item.id}`
            )
        );
    }

    public async patchSubirFotoPregunta(
        encuesta: EncuestaDTO, pregunta: PreguntaDTO, data: FormData
    ): Promise<PreguntaDTO> {
        const response = await firstValueFrom(
            this.httpClient.patch<IPreguntaDTO>(
                `${Constants.apiURL}/combos/${encuesta.id}/questions/${pregunta.id}`, data
            )
        );

        return new PreguntaDTO(response);
    }

    public async deleteFotoPregunta(imageUrl: string): Promise<void> {
        await firstValueFrom(this.httpClient.delete(`${Constants.apiURL}/${imageUrl}`));
    }

    public async postComboMovil(comboMovil: ComboMovilDTO): Promise<void> {
        await firstValueFrom(this.httpClient.post(`${Constants.apiURLV1}/send_combo_for_test`, comboMovil.toJSON()));
    }

    public async postPruebaInterna(pruebaInterna: PruebaInternaDTO): Promise<void> {
        await firstValueFrom(this.httpClient.post(`${Constants.apiURL}/send_internal_test`, pruebaInterna.toJSON()));
    }

    public getTemplates(): Observable<TemplateDTO[]> {
        return this.httpClient.get<ITemplateDTO[]>(`${Constants.apiURL}/combos/templates`).pipe(
            map(templates => templates.map(template => new TemplateDTO(template)))
        );
    }

    public getAccionesItem(encuestaId: number): Observable<AccionesItemDTO[]> {
        return this.httpClient.get<IAccionesItemDTO[]>(`${Constants.apiURL}/combos/${encuestaId}/items-actions`).pipe(
            map(acciones => acciones.map(accion => new AccionesItemDTO(accion)))
        );
    }

    public postAccionItem(
        encuestaId: number, preguntaId: number, itemId: number, accionDTO: AccionesItemDTO
    ): Observable<AccionesItemDTO> {
        return this.httpClient.post<IAccionesItemDTO>(
            `${Constants.apiURL}/combos/${encuestaId}/questions/${preguntaId}/items/${itemId}/actions`, accionDTO.toJSON()
        ).pipe(
            map(accion => new AccionesItemDTO(accion))
        );
    }

    public deleteAccionItem(
        encuestaId: number, preguntaId: number, itemId: number, accionId: number
    ): Observable<void> {
        return this.httpClient.delete<void>(
            `${Constants.apiURL}/combos/${encuestaId}/questions/${preguntaId}/items/${itemId}/actions/${accionId}`
        );
    }

    public postImagenItem(
        encuestaId: number, preguntaId: number, itemId: number, imagenURLDTO: ImagenURLDTO
    ): Observable<PreguntaDTO> {
        return this.httpClient.post<IPreguntaDTO>(
            `${Constants.apiURL}/combos/${encuestaId}/questions/${preguntaId}/items/${itemId}/image`,
            (imagenURLDTO as ImagenURLDTO).imagenURl != null
                ? (imagenURLDTO as ImagenURLDTO).obtenerJSON() : imagenURLDTO
        ).pipe(
            map(res => new PreguntaDTO(res))
        );
    }
}
